import { makeObservable } from 'mobx';

import { FromGamePostMessage, FromParentPostMessage, GameRedirectionSource, SidebarOpenSource, ToGamePostMessage, ToParentPostMessage } from '../enums';
import { ChangeGamePayload, PostMessageModel, SidebarIcon } from '../models';
import { failLaunch, markLaunchStep } from '../services/launch-telemetry';
import { inIframe, isFullscreen } from '../helpers';
import { integration } from '../services';
import { RootStore } from '.';

export class PostMessageStore {

  private rootStore: RootStore;

  iframeElement: HTMLIFrameElement | null = null;

  lobbyIframeElement: HTMLIFrameElement | null = null;

  tournamentIframeElement: HTMLIFrameElement | null = null;

  slotHistoryIframeElement: HTMLIFrameElement | null = null;

  replayIframeElement: HTMLIFrameElement | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this, {});
  }

  public setIframeElement(element: HTMLIFrameElement | null): void {
    this.iframeElement = element;
  };

  public setTournamentIframeElement(element: HTMLIFrameElement): void {
    this.tournamentIframeElement = element;
  };

  public setLobbyIframeElement(element: HTMLIFrameElement): void {
    this.lobbyIframeElement = element;
  };

  public setSlotHistoryIframeElement(element: HTMLIFrameElement): void {
    this.slotHistoryIframeElement = element;
  };

  public setReplayIframeElement(element: HTMLIFrameElement): void {
    this.replayIframeElement = element;
  };

  public sendParentMessage(message: PostMessageModel): void {
    window.parent?.postMessage(message, '*');
  }

  public sendMessage(message: PostMessageModel): void {
    this.iframeElement?.contentWindow?.postMessage(message, '*');
  }

  public sendTournamentMessage(message: PostMessageModel): void {
    this.tournamentIframeElement?.contentWindow?.postMessage(message, '*');
  }

  public sendLobbyMessage(message: PostMessageModel): void {
    this.lobbyIframeElement?.contentWindow?.postMessage(message, '*');
  }
  public sendSlotHistoryMessage(message: PostMessageModel): void {
    this.slotHistoryIframeElement?.contentWindow?.postMessage(message, '*');
  }
  public sendReplayMessage(message: PostMessageModel): void {
    this.replayIframeElement?.contentWindow?.postMessage(message, '*');
  }

  // forwarding another frame's message: an unframed window is its own parent, and sending it back to
  // where it came from lets two launchers ping-pong it - either way the relay never ends
  private relayToParent(message: PostMessageModel, source: MessageEventSource | null): void {
    if (!inIframe() || source === window.parent) return;
    this.sendParentMessage(message);
  }

  private relayToGame(message: PostMessageModel, source: MessageEventSource | null): void {
    if (source === this.iframeElement?.contentWindow) return;
    this.sendMessage(message);
  }

  public handleMessageReceive(message: PostMessageModel | string, source: MessageEventSource | null = null): void {

    // our own echo: sendParentMessage on an unframed window posts to itself, and handling that
    // would send it again forever (e.g. replayFinish with no replay popup open)
    if (source === window) return;

    const launchData = this.rootStore.gameStore.launchData?.data;

    const isMessageString = typeof message === 'string';
    const name = isMessageString ? message : (message.name || message.type);
    const payload = isMessageString ? null : (message.payload || message.type);

    switch (name) {
      case FromGamePostMessage.GameLoaded:
      case FromGamePostMessage.PgGameLoaded:
        markLaunchStep('gameLoaded');
        integration.current.hideGlobalLoader();
        integration.current.handleGameLoaded(launchData, payload?.stakeAmount);
        break;
      case FromGamePostMessage.PgGameLoadFailed:
        this.rootStore.gameStore.handleGameLoadFailed(payload);
        break;
      case FromGamePostMessage.GetAuthInfo:
      case FromGamePostMessage.PgGetAuthInfo:
        // A frame opened late (slot history, lobby, tournament) asks after the token has aged, and the refresh timer
        // is throttled in background tabs: refresh on demand first, as the launcher socket does, so it never gets an
        // expired token. Never rejects; launchData is re-read since the refresh may outlive a game change.
        this.rootStore.authStore.getFreshAccessToken().then(() => {
          integration.current.updateAuthInfo(this.rootStore.gameStore.launchData.data);
          this.sendFullscreenUpdate();
        });
        break;

      case FromGamePostMessage.GameReady:
        integration.current.handleGameReady(payload);
        this.rootStore.userActivityStore.trackGameReady();
        break;

      case FromGamePostMessage.RoundStarted:
        integration.current.handleRoundStarted(payload);
        break;

      case FromGamePostMessage.RoundFinished:
        if (payload?.bet) {
          this.rootStore.userActivityStore.resetIdleTimer();
        }
        integration.current.handleRoundFinished(payload);
        break;

      case FromGamePostMessage.BalanceUpdated:
        integration.current.handleBalanceUpdated(payload);
        break;

      case FromGamePostMessage.WinUpdate:
        integration.current.handleWinUpdate(payload);
        break;

      case FromGamePostMessage.StakeUpdate:
        integration.current.handleStakeUpdate(payload);
        break;

      case FromGamePostMessage.PlayModeUpdate:
        integration.current.handlePlayModeUpdate(payload);
        break;

      case FromGamePostMessage.AutoPlayStarted:
        integration.current.handleAutoPlayStarted();
        break;

      case FromGamePostMessage.AutoPlayFinished:
        integration.current.handleAutoPlayFinished();
        break;

      case FromGamePostMessage.ErrorReceived:
        failLaunch('game_error', payload);
        integration.current.handleErrorReceived(payload);
        break;

      case FromGamePostMessage.PgToggleFullscreen:
        integration.current.handleToggleFullScreen();
        break;

      case FromParentPostMessage.UpdateBalance:
        this.sendUpdateBalance();
        break;

      case ToGamePostMessage.UpdateSettings:
        integration.current.handleSettingsUpdated(payload);
        break;

      case FromParentPostMessage.StopAutoSpins:
        this.sendStopAutoSpins();
        break;

      case FromGamePostMessage.PgRedirectHome:
      case FromGamePostMessage.RedirectHome:
      case FromGamePostMessage.ExitGame:
        integration.current.exitGame(launchData?.exitUrl);
        break;

      case FromGamePostMessage.PgOpenSidebar:
        this.rootStore.sidebarMenuStore.openSidebar(SidebarOpenSource.Game);
        break;
      case FromGamePostMessage.PgSidebarNotificationClick:
        this.rootStore.sidebarMenuStore.openPromotionDetails(payload.type);
        break;
      case FromGamePostMessage.PgTournamentFinished:
        this.rootStore.tournamentStore.setTournamentFinishedModalOpen(true);
        break;

      case FromGamePostMessage.PgTournamentFrameClose:
        this.rootStore.tournamentStore.setTournamentFrameOpen(false);
        break;

      case FromGamePostMessage.PgChangeGame:
        let changeGameParams = payload as ChangeGamePayload;
        if (!payload) {
          changeGameParams = {
            gameId: (message as PostMessageModel).gameId || 0,
            source: GameRedirectionSource.Game
          }
          const tournamentId = (message as PostMessageModel).tournamentId || 0;
          if (tournamentId) {
            changeGameParams.tournamentId = tournamentId;
            changeGameParams.source = GameRedirectionSource.Tournament;
          }
        }
        integration.current.changeGame(changeGameParams);
        // todo: changeGame gameId model in iframe service 
        break;

      case FromGamePostMessage.PgReload:
      case FromGamePostMessage.Reload:
        integration.current.handleReload();
        break;

      case FromGamePostMessage.PgCloseLobby:
        this.rootStore.lobbyStore.setIsLobbyOpen(false);
        break;

      // START: cashier postMessages

      case FromGamePostMessage.PgOpenStream:
        const url = (message as PostMessageModel).url || '';
        if (window.sendString) {
          window.sendString(url);
        } else {
          this.sendParentMessage({
            type: ToParentPostMessage.OpenStream,
            payload: { url }
          });
        }
        break;

      case FromParentPostMessage.ThemeChange:
        this.relayToGame(payload, source);
        break;

      // END: cashier postMessages

      // START: replay postMessages

      case FromGamePostMessage.PgReplayFinish:
        // hosting the replay scene: show the after-replay actions;
        // otherwise this launcher itself runs in replay mode - bubble up to the hosting launcher
        if (this.rootStore.gameStore.replayWin) {
          this.rootStore.gameStore.setIsReplayFinished(true);
        } else {
          this.relayToParent(message as PostMessageModel, source);
        }
        break;

      case FromParentPostMessage.WatchAgain:
        this.relayToGame(message as PostMessageModel, source);
        break;

      // END: replay postMessages

      // START: slot history postMessages
      case FromGamePostMessage.OpenSlotHistory:
        if (payload.type) {
          this.rootStore.slotHistoryStore.setSlotType(payload.type)
        }
        this.rootStore.slotHistoryStore.setIsSlotsHistoryOpen(true);
        this.rootStore.authStore.getRefToken();
        break;

      case FromGamePostMessage.CloseSlotHistory:
        this.rootStore.slotHistoryStore.closeSlotsHistory();
        break;

      case FromGamePostMessage.PgTrackUserActivity:
        this.rootStore.userActivityStore.sendTrackUserActivity(payload);
        break;

      // END: slot history postMessages 
      default:
        break;
    }
  }

  public sendSidebarPostMessage(notifCount = 0) {
    this.sendMessage({
      name: ToGamePostMessage.ShowSidebarOpener,
      payload: { notifCount }
    });
  }

  public sendShowSidebarPromos(activePromos: Array<SidebarIcon>) {
    this.sendMessage({
      name: ToGamePostMessage.ShowSidebarPromos,
      payload: activePromos
    });
  }

  public sendShowSidebarNotification(notification: { type: string, iconUrl: string, message: string }) {
    this.sendMessage({
      name: ToGamePostMessage.ShowSidebarNotification,
      payload: notification
    });
  }

  public sendFullscreenUpdate() {
    this.sendMessage({
      name: ToGamePostMessage.FullscreenChange,
      payload: { isFullscreen: !!isFullscreen() }
    });
  }

  public sendOpenPayTable() {
    this.sendMessage({
      name: ToGamePostMessage.OpenPayTable,
      payload: null
    });
  }

  public sendClosePayTable() {
    this.sendMessage({
      name: ToGamePostMessage.ClosePayTable,
      payload: null
    });
  }

  public sendTogglePayTable() {
    this.sendMessage({
      name: ToGamePostMessage.TogglePayTable,
      payload: null
    });
  }

  public sendOpenGameHelp() {
    this.sendMessage({
      name: ToGamePostMessage.OpenGameHelp,
      payload: null
    });
  }

  public sendCloseGameHelp() {
    this.sendMessage({
      name: ToGamePostMessage.CloseGameHelp,
      payload: null
    });
  }

  public sendToggleGameHelp() {
    this.sendMessage({
      name: ToGamePostMessage.ToggleGameHelp,
      payload: null
    });
  }

  public sendIsBrazilGLA() {
    this.sendMessage({
      name: ToGamePostMessage.IsBrazilGLA,
      payload: null
    });
  }

  public sendPauseGame() {
    this.sendMessage({
      name: ToGamePostMessage.PauseGame,
      payload: null
    });
  }

  public sendStopGame() {
    this.sendMessage({
      name: ToGamePostMessage.StopGame,
      payload: null
    });
  }

  public sendResumeGame() {
    this.sendMessage({
      name: ToGamePostMessage.ResumeGame,
      payload: null
    });
  }

  public sendUpdateBalance() {
    this.sendMessage({
      name: ToGamePostMessage.UpdateBalance,
      payload: null
    });
  }

  public sendUpdateSettings(payload: { sound?: boolean, fastPlay?: boolean }) {
    this.sendMessage({
      name: ToGamePostMessage.UpdateSettings,
      payload
    });
  }

  public sendStopAutoSpins() {
    this.sendMessage({
      name: ToGamePostMessage.StopAutoSpins,
      payload: null
    });
  }

  // the replay-mode launcher inside the frame forwards this into its game iframe
  public sendWatchAgain() {
    this.sendReplayMessage({
      name: FromParentPostMessage.WatchAgain,
      payload: null
    });
  }

}
